import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { Room } from 'app/typings';
import type { ExtranetRoomForm } from 'app/typings/packages';
import { apiMutation, apiRequest } from 'app/utils/request/api';

const path = '/hotels/:id/rooms' as const;
const postMultipleSavePath = '/rooms' as const;
const savePath = '/extranet/:hotelId/rooms' as const;

export function useRooms(id?: number, packagePublishedOnly = false) {
  return useQuery({
    queryKey: [path, { id, packagePublishedOnly }],
    queryFn: !!id
      ? () =>
          apiRequest({
            path,
            params: { id: id.toString() },
            queryParams: { packagePublishedOnly },
          }) as Promise<Room[]>
      : skipToken,
    staleTime: Infinity,
    enabled: !!id,
  });
}

export function useExtranetRoomSave(hotelId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { rooms: ExtranetRoomForm[] }) =>
      apiMutation(
        'POST',
        {
          path: savePath,
          params: { hotelId: hotelId.toString() },
        },
        payload
      ) as Promise<{ roomIds: number[] }>,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [savePath],
      });

      queryClient.invalidateQueries({
        queryKey: [path, { id: hotelId, packagePublishedOnly: false }],
      });
    },
  });
}

export const useSaveBuilderRooms = (hotelId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Room) =>
      apiMutation('POST', { path: postMultipleSavePath }, payload) as Promise<
        []
      >,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [path, { id: hotelId }],
      });
    },
  });
};
